// Vorschau der Wallpaper-Modi außer Einzel. Maße wie renderer/src/scene.rs als Anteil
// der Bildschirmhöhe (cqh). Mini-Bretter statt chessground: bis zu 64 Stück bleiben leicht.
import { h, type VNode } from 'snabbdom';
import type { GameDetail, Ply, WallpaperSettings } from '../api';
import { ASPECT, boardsOf, plyOf, simulGrid } from '../wpmath';

const ROLE: Record<string, string> = { p: 'pawn', n: 'knight', b: 'bishop', r: 'rook', q: 'queen', k: 'king' };
const TRAIL = 10;

const idx = (sq: string) => (8 - +sq[1]) * 8 + (sq.charCodeAt(0) - 97);
const at = (i: number) => ({ left: `${(i % 8) * 12.5}%`, top: `${Math.floor(i / 8) * 12.5}%` });
const surname = (n: string | null) => (n ?? '?').split(',')[0].trim();

function mini(p: Ply, size: number, s: WallpaperSettings, extra: VNode[] = []): VNode {
  const kids: VNode[] = [];
  if (s.showLastMove && p.last) for (const sq of [p.last.slice(0, 2), p.last.slice(2, 4)]) kids.push(h('i.lm', { style: at(idx(sq)) }));
  let i = 0;
  for (const ch of p.fen.split(' ')[0].replace(/\//g, '')) {
    if (/\d/.test(ch)) i += +ch;
    else kids.push(h(`piece.${ch === ch.toUpperCase() ? 'white' : 'black'}.${ROLE[ch.toLowerCase()]}`, { style: at(i++) }));
  }
  return h('div.cg-wrap.mini', { style: { width: `${size}cqh`, height: `${size}cqh` } }, [...kids, ...extra]);
}

type Slot = { g: GameDetail; ply: number };

function slots(games: GameDetail[], n: number, ticks: number): Slot[] {
  return Array.from({ length: n }, (_, k) => {
    const g = games[k % games.length];
    return { g, ply: plyOf(k, n, ticks, g.plies.length) };
  });
}

/** Schwarz oben, Weiß und Jahr unten, wie im Einzel */
function framed(sl: Slot, size: number, s: WallpaperSettings, px: number, extra: VNode[] = []): VNode {
  const g = sl.g.game;
  return h('div.sc-tile', { style: { fontSize: `${px}cqh` } }, [
    s.showNames ? h('div.pl', g.black ?? '?') : null,
    mini(sl.g.plies[sl.ply], size, s, extra),
    s.showNames ? h('div.pl', g.white ?? '?') : null,
    s.showYear && g.year ? h('div.yr', String(g.year)) : null,
  ]);
}

function trail(sl: Slot): VNode {
  const lines: VNode[] = [];
  for (let age = TRAIL - 1; age >= 0; age--) {
    const i = sl.ply - age;
    const last = i > 0 ? sl.g.plies[i].last : null;
    if (!last) continue;
    const [a, b] = [idx(last.slice(0, 2)), idx(last.slice(2, 4))];
    const [x1, y1, x2, y2] = [(a % 8) + 0.5, Math.floor(a / 8) + 0.5, (b % 8) + 0.5, Math.floor(b / 8) + 0.5];
    const fresh = 1 - age / TRAIL, col = i % 2 ? '#f3d7a6' : '#a9c4d8';
    lines.push(h('line', { attrs: { x1, y1, x2, y2, stroke: col, 'stroke-opacity': 0.85 * fresh, 'stroke-width': 0.05 + fresh * 0.07, 'stroke-linecap': 'round' } }));
    lines.push(h('circle', { attrs: { cx: x2, cy: y2, r: 0.07 + fresh * 0.06, fill: col, 'fill-opacity': 0.85 * fresh } }));
  }
  return h('svg.sc-trail', { attrs: { viewBox: '0 0 8 8' } }, lines);
}

function sheet(sl: Slot, s: WallpaperSettings, w: number, hgt: number, px: number): VNode {
  const g = sl.g.game;
  const sans = sl.g.plies.slice(1, sl.ply + 1).map(p => p.san ?? '?');
  const rows: VNode[] = [];
  const fit = Math.max(1, Math.floor((hgt / px - 3.4) / 1.65));
  for (let i = Math.max(0, Math.ceil(sans.length / 2) - fit) * 2; i < sans.length; i += 2) {
    const cell = (j: number) => {
      const age = sans.length - 1 - j;
      return h('span', { class: { cur: age === 0 }, style: { opacity: String(Math.max(0.18, 1 - age * 0.07)) } }, sans[j] ?? '');
    };
    rows.push(h('div.row', [h('span.n', `${i / 2 + 1}.`), cell(i), i + 1 < sans.length ? cell(i + 1) : h('span')]));
  }
  return h('div.sc-sheet', { style: { width: `${w}cqh`, height: `${hgt}cqh`, fontSize: `${px}cqh` } }, [
    s.showNames ? h('div.hd', `${g.white ?? '?'} – ${g.black ?? '?'}`) : null,
    s.showYear && g.year ? h('div.yr', String(g.year)) : null,
    h('div.rows', rows),
  ]);
}

function poster(sl: Slot, s: WallpaperSettings, bs: number): VNode {
  const g = sl.g.game, p = sl.g.plies[sl.ply], n = Math.max(1, Math.ceil(sl.ply / 2));
  const tw = Math.min(ASPECT * 94 - bs - 6, ASPECT * 45);
  return h('div.sc-row.sc-poster', [
    h('div.text', { style: { width: `${tw}cqh` } }, [
      h('div.num', String(n)),
      h('i.bar'),
      s.showNames ? h('div.name', surname(g.white)) : null,
      s.showYear && g.year ? h('div.yr', String(g.year)) : null,
      s.showNames ? h('div.name', surname(g.black)) : null,
      p.san && sl.ply ? h('div.san', `${n}${sl.ply % 2 ? '.' : '…'} ${p.san}`) : null,
    ]),
    mini(p, bs, s),
  ]);
}

/** Bühne für alle Modi außer Einzel; `ticks` zählt die Takte seit dem Laden.
 *  Brettgröße wie im Renderer: aus der Einstellung, nur verkleinert, wenn sie nicht passt. */
export function scenePreview(s: WallpaperSettings, games: GameDetail[], ticks: number, pieces: string): VNode {
  const n = boardsOf(s);
  const sl = slots(games, n, ticks);
  const want = s.boardSize * 100, aw = ASPECT * 94;
  let body: VNode;
  if (s.mode === 'gallery') {
    const gap = Math.max(want * 0.28, 3);
    const px = Math.max(want * 0.045, 1.05);
    body = h('div.sc-row', { style: { gap: `${gap}cqh` } }, sl.map(x => framed(x, Math.min(want, (aw - 2 * gap) / 3, 94 - px * 5.7), s, px)));
  } else if (s.mode === 'simul') {
    const { cols, bs, cw, ch } = simulGrid(n, want);
    // nach plyOf zieht Brett k bei Takt k+1, 2n+k+1, …: das zuletzt gezogene leuchtet
    const lit = ticks > 0 ? (ticks - 1) % n : -1;
    body = h('div.sc-grid', { style: { width: `${cols * cw}cqh`, fontSize: `${Math.max(bs * 0.05, 0.8)}cqh` } },
      sl.map((x, k) => h('div.sc-cell', { class: { lit: k === lit }, style: { width: `${cw}cqh`, height: `${ch}cqh` } }, [
        mini(x.g.plies[x.ply], bs, s),
        h('div.ln', [s.showNames ? `${surname(x.g.game.white)} – ${surname(x.g.game.black)}` : '', s.showYear && x.g.game.year ? `  ${x.g.game.year}` : ''].join('')),
      ])));
  } else if (s.mode === 'sheet') {
    const bs = Math.min(want, aw / 1.75, 94), px = Math.max(bs * 0.035, 1.1);
    body = h('div.sc-row', { style: { gap: `${Math.max(bs * 0.12, 3)}cqh`, alignItems: 'flex-start' } },
      [mini(sl[0].g.plies[sl[0].ply], bs, s), sheet(sl[0], s, Math.max(bs * 0.6, px * 17), Math.min(Math.max(bs, px * 16), 94), px)]);
  } else if (s.mode === 'trail') body = framed(sl[0], Math.min(want, aw, 94 - Math.max(want * 0.045, 1.05) * 5.7), s, Math.max(want * 0.045, 1.05), [trail(sl[0])]);
  else body = poster(sl[0], s, Math.min(want, aw - 6 - ASPECT * 24, 94));
  return h(`div.desk.scene.set-${pieces}.theme-${s.theme}.m-${s.mode}`, { style: { '--dim': String(s.brightness) } as any },
    h(`div.sc-stage.${s.position}`, body));
}
